// API client for the ingestion REST API. CONTRACTS §7.
//
// - Every API call carries the `x-api-key` header (API_KEY).
// - The upload flow is: POST /uploads -> PUT presigned (Content-Type EXACTLY
//   "image/jpeg", no api key — it goes to S3) -> POST /artifacts/{id}/enrich,
//   then poll GET /artifacts/{id} until status != "pending".

import { API_BASE, API_KEY } from "./config";
import { getIdToken, notifyAuthExpired } from "./auth";
import type {
  Artifact,
  CreateUploadRequest,
  CreateUploadResponse,
  ListArtifactsResponse,
  ListStatusQuery,
  UpdateArtifactRequest,
  ApiErrorBody,
} from "./types";

/** The exact Content-Type the server presigns the PUT with. Must match byte-for-byte. */
export const UPLOAD_CONTENT_TYPE = "image/jpeg" as const;

export class ApiError extends Error {
  readonly status: number;
  readonly body: string;
  constructor(status: number, message: string, body: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function apiHeaders(extra?: Record<string, string>): HeadersInit {
  return { "x-api-key": API_KEY, ...extra };
}

async function parseError(res: Response): Promise<never> {
  const text = await res.text().catch(() => "");
  let message = `${res.status} ${res.statusText}`;
  if (text) {
    try {
      const j = JSON.parse(text) as Partial<ApiErrorBody>;
      if (j && typeof j.error === "string") message = j.error;
    } catch {
      // non-JSON body; keep the status line
    }
  }
  throw new ApiError(res.status, message, text);
}

/** Core request helper for the JSON API (adds x-api-key + Cognito token, parses JSON, throws ApiError). */
async function request<T>(
  path: string,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const { json, headers, ...rest } = init ?? {};
  const token = await getIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: apiHeaders({
      ...(token ? { Authorization: token } : {}),
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(headers as Record<string, string> | undefined),
    }),
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  if (!res.ok) {
    // 401 = the Cognito authorizer rejected the token (missing/expired/refresh
    // failed). Drop the session and re-show login. (403 = bad API key / access
    // denied — a build/config issue, NOT session expiry, so don't sign out on it.)
    if (res.status === 401) notifyAuthExpired();
    return parseError(res);
  }
  if (res.status === 204) return undefined as T;
  // Some endpoints (enrich -> 202 {}) may return an empty or small body.
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/** POST /uploads -> { id, key, uploadUrl }. */
export function createUpload(
  body: CreateUploadRequest = { contentType: UPLOAD_CONTENT_TYPE },
): Promise<CreateUploadResponse> {
  return request<CreateUploadResponse>("/uploads", {
    method: "POST",
    json: body,
  });
}

/**
 * PUT the JPEG bytes to the presigned URL. Goes straight to S3 — NO x-api-key.
 * Content-Type MUST equal the signed value ("image/jpeg") byte-for-byte or S3
 * returns 403 SignatureDoesNotMatch.
 */
export async function putImage(
  uploadUrl: string,
  jpeg: Blob,
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": UPLOAD_CONTENT_TYPE },
    body: jpeg,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, `S3 upload failed (${res.status})`, text);
  }
}

/** POST /artifacts/{id}/enrich -> 202. Re-callable on a stuck/bad record. */
export function enrich(id: string): Promise<void> {
  return request<void>(`/artifacts/${encodeURIComponent(id)}/enrich`, {
    method: "POST",
  });
}

/**
 * POST /artifacts/{id}/reupload -> { id, key, uploadUrl }. Presigns a PUT for the
 * EXISTING <id>/original.jpg (in-place "take a better picture"). Same ULID/record.
 */
export function reupload(id: string): Promise<CreateUploadResponse> {
  return request<CreateUploadResponse>(
    `/artifacts/${encodeURIComponent(id)}/reupload`,
    { method: "POST" },
  );
}

/**
 * POST /artifacts/{id}/use-original -> Artifact. Rejects a low-trust reverse
 * image match: the server drops the canonical/thumb and reverts to the original
 * photo placeholder (imageSource="original", imageFidelity="original_placeholder").
 * The original bytes are retained, so it's safe and reversible (re-enrich later).
 */
export function useOriginal(id: string): Promise<Artifact> {
  return request<Artifact>(
    `/artifacts/${encodeURIComponent(id)}/use-original`,
    { method: "POST" },
  );
}

/**
 * POST /artifacts/{id}/use-reverse-candidate {index} -> Artifact. Commits the
 * reviewer-chosen reverse-image candidate (from artifact.reverseImageCandidates)
 * as the new canonical, overriding the auto-picked first match. The human pick IS
 * the verification, so the server does NOT re-run the Gemini verify-gate here
 * (continuation 2026-06-07 P2 "top-5 reverse-image picker").
 */
export function useReverseCandidate(id: string, index: number): Promise<Artifact> {
  return request<Artifact>(
    `/artifacts/${encodeURIComponent(id)}/use-reverse-candidate`,
    { method: "POST", json: { index } },
  );
}

/** GET /artifacts?status= -> { items }. status defaults to "all". */
export async function listArtifacts(
  status: ListStatusQuery = "all",
): Promise<Artifact[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  const r = await request<ListArtifactsResponse>(`/artifacts${qs}`);
  return r.items ?? [];
}

/** GET /artifacts/{id} -> Artifact (404 -> ApiError with status 404). */
export function getArtifact(id: string): Promise<Artifact> {
  return request<Artifact>(`/artifacts/${encodeURIComponent(id)}`);
}

/**
 * PUT /artifacts/{id} -> Artifact. Used for edits and for confirm (status).
 * The server sets fieldSources=manual for edited fields and regenerates
 * catalog.json on confirm / edit-of-confirmed.
 */
export function updateArtifact(
  id: string,
  patch: UpdateArtifactRequest,
): Promise<Artifact> {
  return request<Artifact>(`/artifacts/${encodeURIComponent(id)}`, {
    method: "PUT",
    json: patch,
  });
}

/** DELETE /artifacts/{id} -> 204. */
export function deleteArtifact(id: string): Promise<void> {
  return request<void>(`/artifacts/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Composite flows / helpers
// ---------------------------------------------------------------------------

/**
 * Run the full capture pipeline for one JPEG:
 *   POST /uploads -> PUT presigned -> POST .../enrich.
 * Returns the new artifact id. Does NOT poll (caller decides when/how).
 */
export async function uploadAndEnrich(jpeg: Blob): Promise<string> {
  const { id, uploadUrl } = await createUpload();
  await putImage(uploadUrl, jpeg);
  await enrich(id);
  return id;
}

/**
 * In-place "take a better picture" for an EXISTING artifact:
 *   POST /artifacts/{id}/reupload -> PUT presigned -> POST .../enrich.
 * Same ULID, same record — manual edits are preserved (fieldSources=manual).
 * Does NOT poll (caller decides when/how).
 */
export async function recaptureAndEnrich(id: string, jpeg: Blob): Promise<void> {
  const { uploadUrl } = await reupload(id);
  await putImage(uploadUrl, jpeg);
  await enrich(id);
}

/**
 * Restore ONLY the original photo bytes for an artifact whose <id>/original.jpg
 * is missing/broken — WITHOUT re-enriching:
 *   POST /artifacts/{id}/reupload -> PUT presigned. NO enrich.
 * The enrichment metadata is already correct; we only want the photo back, so the
 * invariant "every artifact has an original photo" holds again (continuation
 * 2026-06-07 P1b). Same ULID/record. Do NOT confuse with use-original, which
 * reverts canonical→original and would make a missing-original item worse.
 */
export async function recaptureOriginalOnly(id: string, jpeg: Blob): Promise<void> {
  const { uploadUrl } = await reupload(id);
  await putImage(uploadUrl, jpeg);
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface PollOptions {
  /** Poll interval in ms (default 2500). */
  intervalMs?: number;
  /** Max attempts before giving up (default 40 ≈ 100s at 2.5s). */
  maxAttempts?: number;
  /** Stop early if this returns true (e.g. component unmounted). */
  signal?: AbortSignal;
  /** Called with each fetched artifact, so the UI can update live. */
  onTick?: (a: Artifact) => void;
}

/**
 * Poll GET /artifacts/{id} until status != "pending" (CONTRACTS §7), then
 * resolve with the artifact. Resolves with the last-seen artifact if it
 * doesn't settle within maxAttempts (caller can inspect status/enrichmentError).
 */
export async function pollUntilSettled(
  id: string,
  opts: PollOptions = {},
): Promise<Artifact> {
  const { intervalMs = 2500, maxAttempts = 40, signal, onTick } = opts;
  let last: Artifact | undefined;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (signal?.aborted) throw new DOMException("aborted", "AbortError");
    last = await getArtifact(id);
    onTick?.(last);
    // Settled — OR a failed enrich, which leaves status=pending but sets
    // enrichmentError; surface that immediately instead of polling for ~100s.
    if (last.status !== "pending" || last.enrichmentError) return last;
    await sleep(intervalMs);
  }
  if (last) return last;
  // Should be unreachable (the loop always assigns last on the first attempt).
  return getArtifact(id);
}
